import * as Y from 'yjs';
import {
  Awareness,
  applyAwarenessUpdate,
  encodeAwarenessUpdate,
} from 'y-protocols/awareness';
import type { NostrEvent, NostrFilter, NostrSigner } from '@nostrify/nostrify';
import {
  finalizeEvent,
  generateSecretKey,
  getEventHash,
  getPublicKey,
  verifyEvent,
} from 'nostr-tools';

import { base64ToBytes, bytesToBase64, decodeContent, encodeContent } from './codec';
import {
  KIND_DOC_METADATA,
  KIND_DOC_PRESENCE,
  KIND_DOC_SNAPSHOT,
  KIND_DOC_UPDATE,
  docAltTag,
  docCoordinate,
} from './kinds';
import {
  buildDocMetadataEvent,
  canPubkeyEdit,
  isPubkeyKnown,
  latestMetadata,
  parseDocMetadata,
  roleForPubkey,
} from './metadata';
import * as storage from './storage';
import type { DocMetadata, PresenceUser, SyncStatus } from './types';

type RelayMsg =
  | readonly ['EVENT', string, NostrEvent]
  | readonly ['EOSE', string]
  | readonly ['CLOSED', string, string];

/** Structural slice of Nostrify's NPool used by the provider. */
export interface DocNostr {
  query(filters: NostrFilter[], opts?: { signal?: AbortSignal }): Promise<NostrEvent[]>;
  req(filters: NostrFilter[], opts?: { signal?: AbortSignal }): AsyncIterable<unknown>;
  event(event: NostrEvent, opts?: { signal?: AbortSignal }): Promise<unknown>;
}

export interface NostrDocProviderOptions {
  ownerPubkey: string;
  docId: string;
  /** Relay group (nostr.group(DOC_RELAYS)) used for all document traffic. */
  nostr: DocNostr;
  /** Current user's signer. Optional — anonymous users get read-only access. */
  signer?: NostrSigner;
  ydoc: Y.Doc;
  awareness: Awareness;
  /** Metadata known locally (freshly created docs) to avoid a flash of "no access". */
  initialMetadata?: DocMetadata;
}

export interface ProviderState {
  status: SyncStatus;
  metadata: DocMetadata | undefined;
  /** Initial sync finished (local cache + relay query round done). */
  ready: boolean;
  role: 'owner' | 'editor' | 'viewer';
  canEdit: boolean;
}

const PUBLISH_DEBOUNCE_MS = 900;
const PUBLISH_MAX_WAIT_MS = 3500;
const SAVE_DEBOUNCE_MS = 500;
const PRESENCE_THROTTLE_MS = 220;
const PRESENCE_HEARTBEAT_MS = 10_000;
const CATCHUP_INTERVAL_MS = 45_000;
const SNAPSHOT_UPDATE_COUNT = 24;
const SNAPSHOT_BYTES = 48_000;
const SNAPSHOT_MAX_AGE_MS = 8 * 60_000;
const SNAPSHOT_CONTENT_LIMIT = 220_000;
const UPDATE_BATCH_LIMIT = 400;

interface BufferedRemote {
  event: NostrEvent;
  bytes: Uint8Array;
  at: number;
}

interface PresenceSession {
  secretKey: Uint8Array;
  pubkey: string;
  /** Identity-signed attestation binding the session key to the user. */
  attestation?: NostrEvent;
}

/**
 * Nostr-powered CRDT document provider.
 *
 * - Document content is a Yjs document. Local edits are applied instantly,
 *   queued, and published to the document relay group as incremental
 *   Yjs updates (kind 2701). Periodically the full state is compacted
 *   into a snapshot (kind 30929).
 * - Live presence & remote cursors travel as ephemeral Yjs awareness
 *   updates (kind 24159). Presence events are signed by a per-session
 *   ephemeral key; the session key is bound to the user's Nostr identity
 *   by a one-time signed attestation embedded in every event, so remote
 *   presence cannot be spoofed without prompting for every cursor move.
 * - Everything is persisted to IndexedDB (full state + unpublished
 *   updates) so editing survives reloads and offline periods.
 */
export class NostrDocProvider {
  readonly ydoc: Y.Doc;
  readonly awareness: Awareness;
  readonly ownerPubkey: string;
  readonly docId: string;

  private readonly nostr: DocNostr;
  private signer: NostrSigner | undefined;
  private readonly coord: string;
  private readonly listeners = new Set<() => void>();

  private state: ProviderState;
  private destroyed = false;
  private ready = false;

  private pendingUpdates: Uint8Array[] = [];
  private publishing = false;
  private publishFailures = 0;
  private publishTimer: ReturnType<typeof setTimeout> | undefined;
  private publishMaxWaitTimer: ReturnType<typeof setTimeout> | undefined;
  private saveTimer: ReturnType<typeof setTimeout> | undefined;

  private pendingTitle: string | undefined;
  private metadataBusy = false;
  private metadataRetry = false;

  private seenEvents = new Set<string>();
  private lastEventAt = 0;
  private latestSnapshotAt = 0;
  private updatesSinceSnapshot = 0;
  private bytesSinceSnapshot = 0;

  private bufferedRemote: BufferedRemote[] = [];
  private unknownPublishers = new Set<string>();
  private unknownPublisherTimer: ReturnType<typeof setTimeout> | undefined;

  private subController: AbortController | undefined;
  private catchupTimer: ReturnType<typeof setInterval> | undefined;

  private presenceSession: PresenceSession | undefined;
  private presenceSessionPromise: Promise<void> | undefined;
  private presenceTimer: ReturnType<typeof setTimeout> | undefined;
  private presenceHeartbeatTimer: ReturnType<typeof setInterval> | undefined;
  private presenceLastSeen = new Map<number, number>();
  private presenceDirty = false;

  constructor(opts: NostrDocProviderOptions) {
    this.ydoc = opts.ydoc;
    this.awareness = opts.awareness;
    this.ownerPubkey = opts.ownerPubkey;
    this.docId = opts.docId;
    this.nostr = opts.nostr;
    this.signer = opts.signer;
    this.coord = docCoordinate(opts.ownerPubkey, opts.docId);

    this.state = this.applyRole({
      status: 'connecting',
      metadata: opts.initialMetadata,
      ready: false,
      role: 'viewer',
      canEdit: false,
    });

    this.ydoc.on('update', this.onDocUpdate);
    this.awareness.on('update', this.onAwarenessUpdate);

    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('pagehide', this.onPageHide);
    document.addEventListener('visibilitychange', this.onPageHide);
  }

  // ------------------------------------------------------------------ state

  getState(): ProviderState {
    return this.state;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private setStatus(status: SyncStatus): void {
    if (this.state.status !== status) {
      this.state = { ...this.state, status };
      this.emit();
    }
  }

  private setMetadata(metadata: DocMetadata | undefined, { cache = true } = {}): void {
    if (metadata && this.state.metadata && this.state.metadata.updatedAt > metadata.updatedAt) {
      return; // never regress to older metadata
    }
    this.state = this.applyRole({ ...this.state, metadata });
    if (metadata && cache) {
      void storage.saveCachedMetadata(metadata);
    }
    this.emit();
    this.processBufferedRemote();
  }

  /** Derive role/canEdit from the signer + metadata into a new state object. */
  private applyRole(state: ProviderState): ProviderState {
    const myPubkey = this.signer?.pubkey;
    const metadata = state.metadata;
    return {
      ...state,
      role: roleForPubkey(myPubkey, metadata),
      canEdit: Boolean(myPubkey && metadata && canPubkeyEdit(myPubkey, metadata)),
    };
  }

  get canPublish(): boolean {
    return Boolean(this.signer) && !this.destroyed;
  }

  /** Update the signer (e.g. the user logged in or out mid-session). */
  setSigner(signer: NostrSigner | undefined): void {
    if (this.signer === signer) return;
    this.signer = signer;
    this.presenceSession = undefined;
    this.presenceSessionPromise = undefined;
    this.state = this.applyRole(this.state);
    this.emit();
    if (signer) {
      void this.ensurePresenceSession();
      if (this.pendingUpdates.length > 0) {
        this.schedulePublish(PUBLISH_DEBOUNCE_MS);
      }
    }
  }

  // ------------------------------------------------------------------ start

  async start(): Promise<void> {
    await this.loadLocal();
    void this.initialSync();
  }

  private async loadLocal(): Promise<void> {
    try {
      const [state, pending, cachedMetadata] = await Promise.all([
        storage.loadDocState(this.ownerPubkey, this.docId),
        storage.loadPendingUpdates(this.ownerPubkey, this.docId),
        this.state.metadata ? Promise.resolve(this.state.metadata) : storage.loadCachedMetadata(this.ownerPubkey, this.docId),
      ]);

      if (this.destroyed) return;

      if (state && state.length > 0) {
        try {
          Y.applyUpdate(this.ydoc, state, this);
        } catch (error) {
          console.warn('[nostr-office] failed to apply cached doc state', error);
        }
      }
      if (pending && pending.length > 0) {
        this.pendingUpdates = [pending];
        this.setStatus('saving');
      }
      if (cachedMetadata) {
        this.setMetadata(cachedMetadata);
      }
    } catch (error) {
      console.warn('[nostr-office] failed to load local state', error);
    }
  }

  private async initialSync(): Promise<void> {
    if (this.destroyed) return;
    this.setStatus(this.state.status === 'saving' ? 'saving' : 'connecting');

    try {
      // Metadata first — it defines the permission boundary for everything else.
      const metadata = await this.fetchMetadata();
      if (metadata) {
        this.setMetadata(metadata);
      }

      const snapshot = await this.fetchSnapshot();
      if (snapshot) {
        await this.applySnapshotEvent(snapshot);
      }

      await this.fetchUpdates(snapshot?.created_at ?? 0);
      this.processBufferedRemote();

      this.ready = true;
      this.state = { ...this.state, ready: true };
      this.emit();

      if (this.pendingUpdates.length > 0) {
        this.schedulePublish(0);
      } else {
        this.setStatus(navigator.onLine ? 'synced' : 'offline');
      }
    } catch (error) {
      console.warn('[nostr-office] initial sync failed', error);
      this.ready = true;
      this.state = { ...this.state, ready: true };
      this.setStatus(navigator.onLine ? 'error' : 'offline');
      this.emit();
    }

    this.startLiveSubscription();
    this.startTimers();
    void this.ensurePresenceSession();
  }

  // ------------------------------------------------------------ fetch logic

  private async fetchMetadata(): Promise<DocMetadata | undefined> {
    if (!navigator.onLine) {
      return this.state.metadata;
    }
    try {
      const events = await this.nostr.query(
        [{ kinds: [KIND_DOC_METADATA], authors: [this.ownerPubkey], '#d': [this.docId], limit: 5 }],
        { signal: AbortSignal.timeout(4000) },
      );
      const metadata = latestMetadata(events);
      if (metadata) {
        this.lastEventAt = Math.max(this.lastEventAt, metadata.updatedAt);
        return metadata;
      }
    } catch (error) {
      console.warn('[nostr-office] metadata fetch failed', error);
    }
    return undefined;
  }

  private async fetchSnapshot(): Promise<NostrEvent | undefined> {
    if (!navigator.onLine) return undefined;
    try {
      const events = await this.nostr.query(
        [{ kinds: [KIND_DOC_SNAPSHOT], '#d': [this.docId], limit: 10 }],
        { signal: AbortSignal.timeout(4000) },
      );
      let best: NostrEvent | undefined;
      for (const event of events) {
        if (!this.isAuthorizedSnapshot(event)) continue;
        if (!best || event.created_at > best.created_at) best = event;
      }
      if (best) {
        this.lastEventAt = Math.max(this.lastEventAt, best.created_at);
      }
      return best;
    } catch (error) {
      console.warn('[nostr-office] snapshot fetch failed', error);
      return undefined;
    }
  }

  private async fetchUpdates(since: number): Promise<void> {
    if (!navigator.onLine) return;
    try {
      const events = await this.nostr.query(
        [{ kinds: [KIND_DOC_UPDATE], '#a': [this.coord], since, limit: UPDATE_BATCH_LIMIT }],
        { signal: AbortSignal.timeout(5000) },
      );
      for (const event of events) {
        await this.applyUpdateEvent(event);
      }
    } catch (error) {
      console.warn('[nostr-office] updates fetch failed', error);
    }
  }

  // ------------------------------------------------------ live subscription

  private liveFilters(): NostrFilter[] {
    return [
      { kinds: [KIND_DOC_UPDATE], '#a': [this.coord] },
      { kinds: [KIND_DOC_METADATA], '#d': [this.docId], authors: [this.ownerPubkey], limit: 1 },
      { kinds: [KIND_DOC_SNAPSHOT], '#d': [this.docId], limit: 5 },
      { kinds: [KIND_DOC_PRESENCE] },
    ];
  }

  private startLiveSubscription(): void {
    if (this.destroyed) return;
    this.subController?.abort();
    const controller = new AbortController();
    this.subController = controller;

    void (async () => {
      while (!this.destroyed && !controller.signal.aborted) {
        try {
          for await (const msg of this.nostr.req(this.liveFilters(), { signal: controller.signal })) {
            const relayMsg = msg as RelayMsg;
            if (relayMsg[0] === 'EVENT') {
              this.handleLiveEvent(relayMsg[2]);
            }
          }
        } catch {
          // Connection dropped — retry below.
        }
        if (this.destroyed || controller.signal.aborted) break;
        await sleep(4000);
        if (this.destroyed || controller.signal.aborted) break;
        // Heal any events missed while the subscription was down.
        void this.resyncAfterReconnect();
      }
    })();
  }

  private handleLiveEvent(event: NostrEvent): void {
    if (this.seenEvents.has(event.id)) return;
    this.seenEvents.add(event.id);
    this.trimSeenEvents();
    this.lastEventAt = Math.max(this.lastEventAt, event.created_at);

    switch (event.kind) {
      case KIND_DOC_UPDATE:
        void this.applyUpdateEvent(event);
        break;
      case KIND_DOC_SNAPSHOT:
        void this.applySnapshotEvent(event);
        break;
      case KIND_DOC_METADATA:
        if (event.pubkey === this.ownerPubkey) {
          const metadata = parseDocMetadata(event);
          if (metadata) this.setMetadata(metadata);
        }
        break;
      case KIND_DOC_PRESENCE:
        this.handlePresenceEvent(event);
        break;
    }
  }

  private trimSeenEvents(): void {
    if (this.seenEvents.size > 5000) {
      this.seenEvents = new Set([...this.seenEvents].slice(-2500));
    }
  }

  // ------------------------------------------------------- event validation

  private isAuthorizedSnapshot(event: NostrEvent): boolean {
    if (event.kind !== KIND_DOC_SNAPSHOT) return false;
    const d = event.tags.find(([name]) => name === 'd')?.[1];
    if (d !== this.docId) return false;
    const metadata = this.state.metadata;
    return metadata ? canPubkeyEdit(event.pubkey, metadata) : event.pubkey === this.ownerPubkey;
  }

  private async applySnapshotEvent(event: NostrEvent): Promise<void> {
    if (!this.isAuthorizedSnapshot(event)) return;
    if (event.created_at <= this.latestSnapshotAt) return;
    const bytes = await decodeContent(event.content);
    if (!bytes || !this.isValidYjsUpdate(bytes)) return;
    this.latestSnapshotAt = event.created_at;
    try {
      Y.applyUpdate(this.ydoc, bytes, this);
    } catch (error) {
      console.warn('[nostr-office] failed to apply snapshot', error);
    }
  }

  private async applyUpdateEvent(event: NostrEvent): Promise<void> {
    if (event.kind !== KIND_DOC_UPDATE) return;
    const a = event.tags.find(([name]) => name === 'a')?.[1];
    if (a !== this.coord) return;

    const metadata = this.state.metadata;
    if (!metadata) {
      // Buffer until metadata arrives so we can check permissions.
      if (this.bufferedRemote.length < 500) {
        const bytes = await decodeContent(event.content);
        if (bytes && this.isValidYjsUpdate(bytes)) {
          this.bufferedRemote.push({ event, bytes, at: Date.now() });
        }
      }
      return;
    }

    if (!canPubkeyEdit(event.pubkey, metadata)) {
      // Possibly a newly added editor we don't know about yet. Refresh
      // metadata once per unknown publisher and buffer meanwhile.
      this.rememberUnknownPublisher(event.pubkey);
      if (this.bufferedRemote.length < 500) {
        const bytes = await decodeContent(event.content);
        if (bytes && this.isValidYjsUpdate(bytes)) {
          this.bufferedRemote.push({ event, bytes, at: Date.now() });
        }
      }
      return;
    }

    const bytes = await decodeContent(event.content);
    if (!bytes || !this.isValidYjsUpdate(bytes)) return;
    this.applyRemoteUpdate(bytes);
  }

  private isValidYjsUpdate(bytes: Uint8Array): boolean {
    // Basic sanity: Yjs updates start with a valid message type byte (0-8).
    return bytes.length > 1 && bytes[0] <= 8;
  }

  private applyRemoteUpdate(bytes: Uint8Array): void {
    try {
      Y.applyUpdate(this.ydoc, bytes, this);
    } catch (error) {
      console.warn('[nostr-office] failed to apply remote update', error);
    }
  }

  private processBufferedRemote(): void {
    if (!this.state.metadata || this.bufferedRemote.length === 0) return;
    const buffer = this.bufferedRemote;
    this.bufferedRemote = [];
    const now = Date.now();
    for (const item of buffer) {
      if (now - item.at > 120_000) continue; // stale
      if (canPubkeyEdit(item.event.pubkey, this.state.metadata)) {
        this.applyRemoteUpdate(item.bytes);
      }
    }
  }

  private rememberUnknownPublisher(pubkey: string): void {
    if (this.unknownPublishers.has(pubkey)) return;
    this.unknownPublishers.add(pubkey);
    if (this.unknownPublisherTimer) return;
    this.unknownPublisherTimer = setTimeout(() => {
      this.unknownPublisherTimer = undefined;
      if (this.destroyed) return;
      void this.refreshMetadata();
    }, 2000);
  }

  private async refreshMetadata(): Promise<void> {
    const metadata = await this.fetchMetadata();
    if (metadata) {
      this.setMetadata(metadata);
      this.processBufferedRemote();
    }
  }

  // -------------------------------------------------------- local edit flow

  private onDocUpdate = (update: Uint8Array, origin: unknown): void => {
    if (origin === this) return; // remote/local-cache apply, already synced
    if (this.destroyed) return;

    this.pendingUpdates.push(update);
    this.scheduleLocalSave();
    if (this.state.canEdit) {
      this.schedulePublish(PUBLISH_DEBOUNCE_MS);
      this.setStatus('saving');
    }
  };

  private scheduleLocalSave(): void {
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => {
      this.saveTimer = undefined;
      void this.saveLocalState();
    }, SAVE_DEBOUNCE_MS);
  }

  private async saveLocalState(): Promise<void> {
    try {
      const state = Y.encodeStateAsUpdate(this.ydoc);
      await storage.saveDocState(this.ownerPubkey, this.docId, state);
      const pending = this.pendingUpdates.length
        ? Y.mergeUpdates(this.pendingUpdates)
        : undefined;
      if (pending) {
        await storage.savePendingUpdates(this.ownerPubkey, this.docId, pending);
      } else {
        await storage.clearPendingUpdates(this.ownerPubkey, this.docId);
      }
    } catch (error) {
      console.warn('[nostr-office] failed to persist local state', error);
    }
  }

  private schedulePublish(delay: number): void {
    if (this.publishTimer) clearTimeout(this.publishTimer);
    this.publishTimer = setTimeout(() => {
      this.publishTimer = undefined;
      void this.flush();
    }, delay);

    if (!this.publishMaxWaitTimer) {
      this.publishMaxWaitTimer = setTimeout(() => {
        this.publishMaxWaitTimer = undefined;
        if (this.pendingUpdates.length > 0 && !this.publishing) {
          if (this.publishTimer) clearTimeout(this.publishTimer);
          this.publishTimer = undefined;
          void this.flush();
        }
      }, PUBLISH_MAX_WAIT_MS);
    }
  }

  /** Publish all pending local updates to the document relay group. */
  async flush(): Promise<void> {
    if (this.destroyed || this.publishing) return;
    if (this.pendingUpdates.length === 0) {
      if (this.state.status === 'saving') this.setStatus(navigator.onLine ? 'synced' : 'offline');
      return;
    }
    if (!this.signer) {
      // Should not happen — editor is read-only without a signer.
      return;
    }
    if (!this.state.canEdit) {
      // Lost edit permission (e.g. demoted to viewer while offline). Keep
      // the local changes but do not publish; user data is never deleted.
      return;
    }

    this.publishing = true;
    let failure: unknown;
    try {
      const update = Y.mergeUpdates(this.pendingUpdates);
      const content = await encodeContent(update);
      const event = await this.signer.signEvent({
        kind: KIND_DOC_UPDATE,
        content,
        tags: [
          ['a', this.coord],
          docAltTag(this.state.metadata?.title ?? 'Untitled document'),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      await this.nostr.event(event, { signal: AbortSignal.timeout(7000) });

      // Published successfully — the pending updates are now on relays.
      this.pendingUpdates = [];
      this.publishFailures = 0;
      await storage.clearPendingUpdates(this.ownerPubkey, this.docId);

      this.updatesSinceSnapshot += 1;
      this.bytesSinceSnapshot += update.length;
      this.lastEventAt = Math.max(this.lastEventAt, event.created_at);

      if (this.shouldCompactSnapshot()) {
        await this.publishSnapshot();
      }
    } catch (error) {
      failure = error;
      console.warn('[nostr-office] publish failed', error);
    } finally {
      this.publishing = false;
    }

    if (failure) {
      this.publishFailures += 1;
      this.setStatus(navigator.onLine ? 'error' : 'offline');
      this.schedulePublish(backoffDelay(this.publishFailures));
    } else {
      this.setStatus(navigator.onLine
        ? (this.pendingUpdates.length > 0 ? 'saving' : 'synced')
        : 'offline');
      if (this.pendingUpdates.length > 0) {
        this.schedulePublish(PUBLISH_DEBOUNCE_MS);
      }
    }
  }

  private shouldCompactSnapshot(): boolean {
    return (
      this.updatesSinceSnapshot >= SNAPSHOT_UPDATE_COUNT ||
      this.bytesSinceSnapshot >= SNAPSHOT_BYTES ||
      (this.updatesSinceSnapshot > 0 && Date.now() - this.latestSnapshotAt > SNAPSHOT_MAX_AGE_MS)
    );
  }

  /** Publish the full document state as a compaction snapshot. */
  async publishSnapshot(): Promise<void> {
    if (this.destroyed || !this.signer || !this.state.canEdit) return;
    const state = Y.encodeStateAsUpdate(this.ydoc);
    const content = await encodeContent(state);
    if (content.length > SNAPSHOT_CONTENT_LIMIT) {
      return; // too large for relays; updates still carry the content
    }
    try {
      const event = await this.signer.signEvent({
        kind: KIND_DOC_SNAPSHOT,
        content,
        tags: [
          ['d', this.docId],
          ['a', this.coord],
          docAltTag(this.state.metadata?.title ?? 'Untitled document'),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      await this.nostr.event(event, { signal: AbortSignal.timeout(8000) });
      this.seenEvents.add(event.id);
      this.latestSnapshotAt = Math.max(this.latestSnapshotAt, event.created_at);
      this.updatesSinceSnapshot = 0;
      this.bytesSinceSnapshot = 0;
    } catch (error) {
      console.warn('[nostr-office] snapshot publish failed', error);
    }
  }

  // ------------------------------------------------------------- metadata

  /** Optimistically rename the document (owner only). Publishes debounced. */
  setTitle(title: string): void {
    const metadata = this.state.metadata;
    if (!metadata) return;
    if (this.signer?.pubkey !== this.ownerPubkey) return;
    const trimmed = title.slice(0, 200);
    this.pendingTitle = trimmed;
    this.setMetadata({ ...metadata, title: trimmed }, { cache: true });
    if (this.metadataBusy) {
      this.metadataRetry = true;
      return;
    }
    if (this.titleTimer) clearTimeout(this.titleTimer);
    this.titleTimer = setTimeout(() => {
      this.titleTimer = undefined;
      void this.publishMetadataChanges();
    }, 900);
  }

  private titleTimer: ReturnType<typeof setTimeout> | undefined;

  /** Replace the collaborator list (owner only). Fetches the latest
   * metadata first so concurrent changes are not clobbered. */
  async updateCollaborators(updater: (current: DocMetadata) => DocMetadata['collaborators']): Promise<boolean> {
    if (!this.signer || this.signer.pubkey !== this.ownerPubkey) return false;
    this.metadataBusy = true;
    try {
      const fresh = (await this.fetchMetadata()) ?? this.state.metadata;
      if (!fresh) return false;
      const collaborators = updater(fresh);
      this.pendingTitle = undefined; // title change is folded into this publish
      const event = await this.signer.signEvent(
        buildDocMetadataEvent({
          docId: this.docId,
          title: fresh.title,
          collaborators,
          createdAt: fresh.createdAt,
          deleted: fresh.deleted,
        }),
      );
      await this.nostr.event(event, { signal: AbortSignal.timeout(6000) });
      this.seenEvents.add(event.id);
      const published = parseDocMetadata(event);
      if (published) this.setMetadata(published);
      return true;
    } catch (error) {
      console.warn('[nostr-office] collaborator update failed', error);
      return false;
    } finally {
      this.metadataBusy = false;
      if (this.metadataRetry) {
        this.metadataRetry = false;
        void this.publishMetadataChanges();
      }
    }
  }

  private async publishMetadataChanges(): Promise<void> {
    if (this.destroyed || this.metadataBusy) return;
    if (this.pendingTitle === undefined) return;
    if (!this.signer || this.signer.pubkey !== this.ownerPubkey) return;
    this.metadataBusy = true;
    const title = this.pendingTitle;
    try {
      const fresh = (await this.fetchMetadata()) ?? this.state.metadata;
      if (!fresh) return;
      if (fresh.title === title) {
        this.pendingTitle = undefined;
        return;
      }
      const event = await this.signer.signEvent(
        buildDocMetadataEvent({
          docId: this.docId,
          title,
          collaborators: fresh.collaborators,
          createdAt: fresh.createdAt,
          deleted: fresh.deleted,
        }),
      );
      await this.nostr.event(event, { signal: AbortSignal.timeout(6000) });
      this.seenEvents.add(event.id);
      if (this.pendingTitle === title) this.pendingTitle = undefined;
      const published = parseDocMetadata(event);
      if (published) this.setMetadata(published);
    } catch (error) {
      console.warn('[nostr-office] title publish failed — will retry when online', error);
      // Retry on the next flush cycle / online event.
    } finally {
      this.metadataBusy = false;
    }
  }

  // ------------------------------------------------------------- presence

  /**
   * Prepare the presence session: an ephemeral keypair that signs every
   * presence event (so the user's signer is not prompted on every cursor
   * move), plus a one-time identity attestation binding the session key
   * to the user's Nostr identity.
   */
  private ensurePresenceSession(): Promise<void> {
    if (!this.signer || this.presenceSessionPromise) {
      return this.presenceSessionPromise ?? Promise.resolve();
    }
    const signer = this.signer;
    this.presenceSessionPromise = (async () => {
      const secretKey = generateSecretKey();
      const pubkey = getPublicKey(secretKey);
      try {
        const attestation = await signer.signEvent({
          kind: KIND_DOC_PRESENCE,
          content: pubkey,
          tags: [
            ['alt', 'Presence session attestation'],
            ['session', pubkey],
          ],
          created_at: Math.floor(Date.now() / 1000),
        });
        this.presenceSession = { secretKey, pubkey, attestation };
      } catch {
        // User declined the one-time prompt (or signer unavailable):
        // presence will not be broadcast, viewing/editing still works.
        this.presenceSession = { secretKey, pubkey };
      }
      if (this.presenceDirty) {
        this.presenceDirty = false;
        this.sendPresenceUpdate();
      }
    })();
    return this.presenceSessionPromise;
  }

  private onAwarenessUpdate = (
    { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ): void => {
    // Track liveness for display purposes (the Awareness protocol itself
    // evicts participants that stopped broadcasting after ~30s).
    const now = Date.now();
    if (origin === 'remote') {
      for (const client of added) this.presenceLastSeen.set(client, now);
      for (const client of updated) this.presenceLastSeen.set(client, now);
      return;
    }
    if (origin !== 'local') return; // e.g. 'timeout' evictions of remote peers

    // Local change (own cursor / user data) — queue a broadcast.
    const clients = [...added, ...updated, ...removed];
    if (clients.length === 0) return;
    void this.ensurePresenceSession().then(() => {
      if (this.destroyed) return;
      if (this.presenceTimer) return;
      this.presenceTimer = setTimeout(() => {
        this.presenceTimer = undefined;
        this.sendPresenceUpdate();
      }, PRESENCE_THROTTLE_MS);
    });
  };

  private sendPresenceUpdate(): void {
    const session = this.presenceSession;
    if (!session?.attestation || this.destroyed || !navigator.onLine) {
      if (!session) this.presenceDirty = true;
      return;
    }
    const clients = [this.awareness.clientID];
    const update = encodeAwarenessUpdate(this.awareness, clients);
    const payload = JSON.stringify({
      v: 1 as const,
      a: this.coord,
      d: bytesToBase64(update),
      att: session.attestation,
    });
    const event = finalizeEvent(
      {
        kind: KIND_DOC_PRESENCE,
        content: payload,
        tags: [],
        created_at: Math.floor(Date.now() / 1000),
      },
      session.secretKey,
    );
    this.nostr.event(event, { signal: AbortSignal.timeout(3000) }).catch(() => {
      // Presence is best-effort.
    });
  }

  private handlePresenceEvent(event: NostrEvent): void {
    let payload: { v?: number; a?: string; d?: string; att?: unknown };
    try {
      payload = JSON.parse(event.content);
    } catch {
      return;
    }
    if (payload?.v !== 1 || payload.a !== this.coord || typeof payload.d !== 'string') return;
    if (!this.isValidPresencePayload(event, payload)) return;

    let bytes: Uint8Array;
    try {
      bytes = base64ToBytes(payload.d);
    } catch {
      return;
    }
    try {
      applyAwarenessUpdate(this.awareness, bytes, 'remote');
    } catch {
      // Malicious/garbage awareness payload — ignore.
    }
  }

  /**
   * Validate a presence event: the wrapper must be signed by the session
   * key, and the embedded attestation must be a valid, identity-signed
   * event binding that session key. Presence without a valid attestation
   * is dropped.
   */
  private isValidPresencePayload(
    event: NostrEvent,
    payload: { att?: unknown },
  ): boolean {
    const att = payload.att as NostrEvent | undefined;
    if (!att || typeof att !== 'object') return false;
    if (typeof att.kind !== 'number' || typeof att.content !== 'string' || typeof att.pubkey !== 'string') return false;
    if (att.kind !== KIND_DOC_PRESENCE) return false;
    if (att.content !== event.pubkey) return false; // attestation must bind this session key
    try {
      // Verify the attestation's identity signature.
      if (att.id !== getEventHash(att)) return false;
      if (!verifyEvent(att)) return false;
    } catch {
      return false;
    }
    // The identity must be a known participant of the document.
    const metadata = this.state.metadata;
    if (!metadata || !isPubkeyKnown(att.pubkey, metadata)) return false;
    return true;
  }

  /** Users currently present (from awareness state), excluding stale ones. */
  getPresenceUsers(): PresenceUser[] {
    const now = Date.now();
    const users: PresenceUser[] = [];
    for (const [clientID, state] of this.awareness.getStates()) {
      if (clientID === this.awareness.clientID) continue;
      if (state.user) {
        users.push({
          clientID,
          pubkey: state.user.pubkey,
          name: state.user.name ?? 'Anonymous',
          color: state.user.color ?? '#64748b',
          picture: state.user.picture,
          lastSeen: this.presenceLastSeen.get(clientID) ?? now,
        });
      }
    }
    return users;
  }

  private startTimers(): void {
    this.stopTimers();

    // Periodic catch-up query heals any events missed during reconnect gaps.
    this.catchupTimer = setInterval(() => {
      if (this.destroyed || !navigator.onLine) return;
      void (async () => {
        const metadata = await this.fetchMetadata();
        if (metadata) this.setMetadata(metadata);
        await this.fetchUpdates(Math.max(0, this.lastEventAt - 120));
        this.processBufferedRemote();
        if (this.pendingTitle !== undefined) {
          void this.publishMetadataChanges();
        }
        if (this.pendingUpdates.length > 0 && !this.publishing) {
          void this.flush();
        }
      })();
    }, CATCHUP_INTERVAL_MS);

    // Presence heartbeat: lets newcomers discover existing participants
    // (ephemeral events are not stored, so late joiners see nothing until
    // each participant re-broadcasts). The Awareness protocol also renews
    // its local clock every ~15s, which triggers additional broadcasts.
    this.presenceHeartbeatTimer = setInterval(() => {
      if (this.destroyed) return;
      this.sendPresenceUpdate();
    }, PRESENCE_HEARTBEAT_MS);
  }

  private stopTimers(): void {
    if (this.catchupTimer) clearInterval(this.catchupTimer);
    if (this.presenceHeartbeatTimer) clearInterval(this.presenceHeartbeatTimer);
    this.catchupTimer = undefined;
    this.presenceHeartbeatTimer = undefined;
  }

  // ---------------------------------------------------------- connectivity

  private onOnline = (): void => {
    if (this.destroyed) return;
    void this.resyncAfterReconnect();
  };

  private onOffline = (): void => {
    if (this.destroyed) return;
    this.setStatus('offline');
  };

  private async resyncAfterReconnect(): Promise<void> {
    if (this.destroyed) return;
    this.setStatus(this.pendingUpdates.length > 0 ? 'saving' : 'synced');
    this.startLiveSubscription();
    try {
      const metadata = await this.fetchMetadata();
      if (metadata) this.setMetadata(metadata);
      await this.fetchUpdates(Math.max(0, this.lastEventAt - 120));
      this.processBufferedRemote();
      if (this.pendingUpdates.length > 0) {
        this.publishFailures = 0;
        await this.flush();
      }
      if (this.pendingTitle !== undefined) {
        void this.publishMetadataChanges();
      }
      this.setStatus(
        this.pendingUpdates.length > 0 ? 'saving' : navigator.onLine ? 'synced' : 'offline',
      );
    } catch {
      this.setStatus(navigator.onLine ? 'error' : 'offline');
    }
  }

  private onPageHide = (e: Event): void => {
    if (this.destroyed) return;
    if (e.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
    // Persist immediately and tell peers we're leaving.
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = undefined;
    }
    void this.saveLocalState();
    this.sendPresenceUpdate();
  };

  // ------------------------------------------------------------- lifecycle

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    window.removeEventListener('pagehide', this.onPageHide);
    document.removeEventListener('visibilitychange', this.onPageHide);

    this.ydoc.off('update', this.onDocUpdate);
    this.awareness.off('update', this.onAwarenessUpdate);

    if (this.publishTimer) clearTimeout(this.publishTimer);
    if (this.publishMaxWaitTimer) clearTimeout(this.publishMaxWaitTimer);
    if (this.saveTimer) clearTimeout(this.saveTimer);
    if (this.titleTimer) clearTimeout(this.titleTimer);
    if (this.presenceTimer) clearTimeout(this.presenceTimer);
    if (this.unknownPublisherTimer) clearTimeout(this.unknownPublisherTimer);
    this.stopTimers();

    this.subController?.abort();

    // Final persist.
    void this.saveLocalState();
    try {
      this.ydoc.destroy();
    } catch {
      // Y.Doc destroy races with editor teardown; safe to ignore.
    }
    try {
      this.awareness.destroy();
    } catch {
      // Same.
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffDelay(failures: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.min(failures, 5));
}
